/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { Dimension, MarkerShapes } from '#utils/UNSAFE_visTypes/common';
import type {
  DataCursorPosition,
  DataCursorStyle,
  OnDataCursorPositionChange,
  Scale,
  Info
} from '#utils/UNSAFE_visTypes/chart';
import { getDataCursorCoords, getMarkerCoords } from './util';
import { styles } from './themes/DataCursorStyles.css';
import { Marker } from '#PRIVATE_SvgShapes';
import { useRef } from 'preact/hooks';
import type { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { colorSchemeVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { rgb } from '#UNSAFE_Theme';
import { useVisHover } from '#hooks/PRIVATE_useVisHover';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

type Props = {
  touchResponse: 'touchStart' | 'touchHold';
  findNearest: (x: number, y: number, y2?: number) => any;
  dataCursorPosition?: DataCursorPosition;
  isDataCursorEnabled?: boolean;
  isHoriz: boolean;
  plotAreaSpace: Dimension;
  yScale: Scale;
  y2Scale: Scale;
  xScale: Scale;
  onDataCursorPositionChange?: OnDataCursorPositionChange;
  dataCursorStyle?: DataCursorStyle;
  focusedItemInfo: Info;
  getDataItemPos: (seriesIndex: number, groupIndex: number) => Dimension | undefined;
  getMarkerInfo: (
    seriesIndex: number,
    groupIndex: number
  ) => { type?: MarkerShapes; color: ColorProps['color'] };
};

export function useChartDataCursor({
  touchResponse,
  findNearest,
  dataCursorPosition,
  onDataCursorPositionChange,
  isDataCursorEnabled,
  isHoriz,
  plotAreaSpace,
  yScale,
  y2Scale,
  xScale,
  dataCursorStyle,
  focusedItemInfo,
  getDataItemPos,
  getMarkerInfo
}: Props) {
  let dataCursorContent = null;
  let x = dataCursorPosition?.x;
  const y = dataCursorPosition?.y;
  const y2 = dataCursorPosition?.y2;
  const isSnap = dataCursorStyle?.behavior === 'snap';

  const itemInfo = useRef<{ x: number; y: number; groupIndex: number; seriesIndex: number }>();

  const updateDataCursor = () => {
    const { seriesIndex, groupIndex } = focusedItemInfo;
    const elemBounds = getDataItemPos(seriesIndex!, groupIndex!);
    if (!elemBounds) {
      return;
    }
    const px = elemBounds?.x + elemBounds.width / 2;
    const py = elemBounds?.y + elemBounds.height / 2;

    let X = xScale.invert(isHoriz ? py : px);
    let Y = yScale?.invert(isHoriz ? px : py);
    let Y2 = y2Scale?.invert(isHoriz ? px : py);

    const neareastItemInfo = findNearest(X, Y, Y2);

    if (!neareastItemInfo) {
      return;
    }

    itemInfo.current = neareastItemInfo;
    if (isSnap) {
      X = xScale.invert(neareastItemInfo.x);
      Y = yScale?.invert(neareastItemInfo.y);
      Y2 = y2Scale?.invert(neareastItemInfo.y2);
    }
    onDataCursorPositionChange?.({ x: X, y: Y, y2: Y2 });
  };

  const onHover = (event: PointerEvent) => {
    const px = event.offsetX;
    const py = event.offsetY;
    if (
      px < plotAreaSpace.x ||
      py < plotAreaSpace.y ||
      px > plotAreaSpace.x + plotAreaSpace.width ||
      py > plotAreaSpace.y + plotAreaSpace.height
    ) {
      dataCursorContent = null;
      onDataCursorPositionChange?.({ x: undefined, y: undefined, y2: undefined });
      return;
    }

    let X = xScale.invert(isHoriz ? py : px);
    let Y = yScale?.invert(isHoriz ? px : py);
    let Y2 = y2Scale?.invert(isHoriz ? px : py);

    const neareastItemInfo = findNearest(X, Y, Y2);

    if (!neareastItemInfo) {
      return;
    }

    itemInfo.current = neareastItemInfo;
    if (isSnap) {
      X = xScale.invert(neareastItemInfo.x);
      Y = yScale.invert(neareastItemInfo.y);
      Y2 = y2Scale.invert(neareastItemInfo.y2);
    }
    onDataCursorPositionChange?.({ x: X, y: Y, y2: Y2 });
  };

  const onHoverLeave = () => {
    onDataCursorPositionChange?.({ x: undefined, y: undefined, y2: undefined });
    itemInfo.current = undefined;
  };

  const hoverHandlers = useVisHover(onHover, undefined, onHoverLeave, touchResponse);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      onDataCursorPositionChange?.({ x: undefined, y: undefined, y2: undefined });
      itemInfo.current = undefined;
      return;
    }
    updateDataCursor();
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Tab' && focusedItemInfo.isCurrent) {
      return;
    }
    updateDataCursor();
  };

  if (x != undefined && (y != undefined || y2 != undefined) && isDataCursorEnabled) {
    const info = itemInfo.current || findNearest(x, y!, y2);
    if (isSnap) {
      x = xScale.invert(info.x);
    }
    const { mainLineProps, contrastLineProps } = getDataCursorCoords(
      xScale,
      x,
      plotAreaSpace,
      isHoriz,
      dataCursorStyle
    );
    const { type, color } = getMarkerInfo(info.seriesIndex!, info.groupIndex!);
    const markerType = type || 'circle';
    // TODO: JET-65336
    const markerDims = markerType === 'circle' ? 18 : 16;

    const { tx, ty } = getMarkerCoords(info, isHoriz);

    dataCursorContent = (
      <>
        <g class={styles.dataCursor}>
          <line {...mainLineProps} class={styles.dataCursorLine} />
          <line {...contrastLineProps} class={styles.contrastLine} />
        </g>
        {dataCursorStyle?.isMarkerDisplayed && info && (
          <Marker
            type={markerType}
            tx={tx}
            ty={ty}
            isInteractive={true}
            scale={1}
            fill={color}
            stroke={rgb(colorSchemeVars.palette.neutral[0])}
            outerStroke={color}
            width={markerDims}
            height={markerDims}
            preserveRectAspectRatio
          />
        )}
      </>
    );
  }

  return {
    dataCursorContent,
    dataCursorProps: isDataCursorEnabled
      ? mergeProps(hoverHandlers, {
          onKeyDown,
          onKeyUp
        })
      : {}
  };
}
